import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router-dom";

import { auth as authApi } from "../api/endpoints";
import AuthShell from "../components/AuthShell.jsx";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const uid = params.get("uid");
  const token = params.get("token");
  return uid && token ? (
    <ConfirmForm uid={uid} token={token} />
  ) : (
    <RequestForm />
  );
}

function RequestForm() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setBusy(true);
    try {
      await authApi.passwordReset(email);
      setSent(true);
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title={t("auth.resetTitle")}
      subtitle={t("auth.resetSubtitle")}
      footer={
        <Link to="/login" className="text-brand-700 hover:underline">
          ← {t("auth.resetBackToLogin")}
        </Link>
      }
    >
      {sent ? (
        <div className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700 ring-1 ring-emerald-200">
          {t("auth.resetSent")}
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <input
            type="email"
            className="input"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            required
            autoFocus
          />
          <button type="submit" className="btn-primary w-full" disabled={busy}>
            {busy ? t("auth.resetBusy") : t("auth.resetButton")}
          </button>
        </form>
      )}
    </AuthShell>
  );
}

function ConfirmForm({ uid, token }) {
  const { t } = useTranslation();
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [error, setError] = useState(null);
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  async function onSubmit(e) {
    e.preventDefault();
    setError(null);
    if (pwd !== pwd2) {
      setError(t("auth.passwordMismatch"));
      return;
    }
    setBusy(true);
    try {
      await authApi.passwordResetConfirm({ uid, token, new_password: pwd });
      setDone(true);
    } catch (err) {
      setError(err?.response?.data?.detail || t("auth.resetInvalidLink"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <AuthShell
      title={t("auth.resetConfirmTitle")}
      footer={
        <Link to="/login" className="text-brand-700 hover:underline">
          ← {t("auth.resetBackToLogin")}
        </Link>
      }
    >
      {done ? (
        <div className="space-y-4">
          <div className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700 ring-1 ring-emerald-200">
            {t("auth.resetDone")}
          </div>
          <Link to="/login" className="btn-primary w-full">
            {t("common.signIn")}
          </Link>
        </div>
      ) : (
        <form onSubmit={onSubmit} className="space-y-4">
          <input
            type="password"
            className="input"
            placeholder={t("auth.resetNewPassword")}
            value={pwd}
            onChange={(e) => setPwd(e.target.value)}
            minLength={8}
            required
          />
          <input
            type="password"
            className="input"
            placeholder={t("auth.resetConfirmNewPassword")}
            value={pwd2}
            onChange={(e) => setPwd2(e.target.value)}
            minLength={8}
            required
          />
          {error && (
            <div className="rounded-md bg-red-50 px-3 py-2 text-sm text-red-700 ring-1 ring-red-200">
              {error}
            </div>
          )}
          <button type="submit" className="btn-primary w-full" disabled={busy}>
            {busy ? t("auth.resetUpdateBusy") : t("auth.resetUpdateButton")}
          </button>
        </form>
      )}
    </AuthShell>
  );
}
